import { useState, useEffect, useCallback, useRef } from 'react';
import { eligibilityRefreshService, RefreshStatus } from '../services/eligibilityRefreshService';

/**
 * Hook for managing eligibility refresh operations
 * Automatically triggers refresh on login if needed and polls for progress
 */

export interface UseEligibilityRefreshReturn {
  status: RefreshStatus;
  isLoading: boolean;
  error: string | null;
  progress: number;
  isActive: boolean;
  isCompleted: boolean;
  hasError: boolean;
  statusMessage: string;
  startRefresh: () => Promise<void>;
  cancelRefresh: () => Promise<void>;
  checkStatus: () => Promise<void>;
}

export const useEligibilityRefresh = (): UseEligibilityRefreshReturn => {
  const [status, setStatus] = useState<RefreshStatus>({ status: 'idle' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Start a refresh operation
   */
  const startRefresh = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await eligibilityRefreshService.startRefresh();
      
      if (result.success) {
        console.log('✅ Eligibility refresh started successfully');
        // Start polling for status updates
        startPolling();
      } else {
        setError(result.error || 'Failed to start refresh');
      }
    } catch (err: any) {
      console.error('❌ Error starting refresh:', err);
      setError(err.message || 'Failed to start refresh');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Cancel the current refresh operation
   */
  const cancelRefresh = useCallback(async () => {
    try {
      await eligibilityRefreshService.cancelRefresh();
      stopPolling();
      setStatus({ status: 'cancelled' });
    } catch (err: any) {
      console.error('❌ Error cancelling refresh:', err);
      setError(err.message || 'Failed to cancel refresh');
    }
  }, []);

  /**
   * Check the current status
   */
  const checkStatus = useCallback(async () => {
    try {
      const currentStatus = await eligibilityRefreshService.getStatus();
      setStatus(currentStatus);
      
      // Stop polling if operation is complete
      if (!eligibilityRefreshService.isActive(currentStatus)) {
        stopPolling();
      }
    } catch (err: any) {
      console.error('❌ Error checking status:', err);
      setError(err.message || 'Failed to check status');
    }
  }, []);

  /**
   * Start polling for status updates
   */
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    pollingIntervalRef.current = setInterval(() => {
      checkStatus();
    }, 2000); // Poll every 2 seconds
  }, [checkStatus]);

  /**
   * Stop polling for status updates
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Check status on mount
  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // Computed values
  const progress = eligibilityRefreshService.getProgressPercentage(status);
  const isActive = eligibilityRefreshService.isActive(status);
  const isCompleted = eligibilityRefreshService.isCompleted(status);
  const hasError = eligibilityRefreshService.hasError(status);
  const statusMessage = eligibilityRefreshService.getStatusMessage(status);

  return {
    status,
    isLoading,
    error,
    progress,
    isActive,
    isCompleted,
    hasError,
    statusMessage,
    startRefresh,
    cancelRefresh,
    checkStatus,
  };
};
